// Diversified Outreach admin tab — dashboard + import + contact segment assignment
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Loader2, Upload, Layers, User, Building2, X as XIcon, Send,
} from "lucide-react";
import { toast } from "sonner";
import { adminFetch, adminPost } from "@/lib/api";

const SEG_LABEL = { individual: "Individual", organizational: "Organizational" };

export default function DiversifiedOutreach({ pw, emailUsage }) {
  const [dashboard, setDashboard] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = async (f = filter) => {
    setLoading(true);
    try {
      const [dash, cs] = await Promise.all([
        adminFetch("/admin/diversified/dashboard", pw),
        adminFetch(`/admin/diversified/contacts${f && f !== "all" ? `?segment=${f}` : ""}`, pw),
      ]);
      setDashboard(dash);
      setContacts(cs.contacts || []);
      setSelected([]);
    } catch {
      toast.error("Failed to load Diversified Outreach data");
    } finally { setLoading(false); }
  };

  const isSelected = (email) => selected.includes(email);
  function toggleSelect(email) {
    setSelected(isSelected(email) ? selected.filter((e) => e !== email) : [...selected, email]);
  }
  function selectAll() { setSelected(contacts.map((c) => c.email)); }
  function clearSel() { setSelected([]); }

  useEffect(() => { load(filter); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  async function assign(segment, emails = null) {
    const targets = emails || selected;
    if (targets.length === 0) { toast.error("Select at least one contact."); return; }
    setBusy(true);
    try {
      await adminPost("/admin/diversified/segment", pw, { emails: targets, segment });
      toast.success(segment ? `Tagged ${targets.length} as ${SEG_LABEL[segment]}.` : `Cleared ${targets.length}.`);
      await load(filter);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assignment failed.");
    } finally { setBusy(false); }
  }

  async function runDripNow() {
    if ((emailUsage?.percent_used ?? 0) >= 90) {
      toast.error(`Resend usage is at ${emailUsage.percent_used}% — bulk sends paused.`);
      return;
    }
    const ok = window.confirm(
      "Send all queued Diversified Outreach emails now?\n\n" +
        "• Fires the drip immediately (no need to wait for the hourly tick).\n" +
        "• Up to 40 emails go out per click; click again after a minute to drain the rest.\n" +
        "• Only contacts whose next-send time has arrived will receive an email.\n\n" +
        "Cannot be undone."
    );
    if (!ok) return;
    setBusy(true);
    try {
      await adminPost("/admin/diversified/run", pw);
      toast.success("Diversified drip fired. Check your Resend dashboard for delivery details.");
      await load(filter);
    } catch (e) {
      toast.error(e?.message || "Failed to run drip.");
    } finally { setBusy(false); }
  }

  if (loading || !dashboard) {
    return <div className="py-16 text-center text-ink/50"><Loader2 className="inline-block mr-2 animate-spin" size={14} /> Loading…</div>;
  }

  const d = dashboard;
  return (
    <div className="space-y-6" data-testid="diversified-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow mb-1">{d.list_name}</div>
          <h2 className="font-display text-2xl tracking-[-0.025em]">Campaign dashboard</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={runDripNow}
            disabled={busy || (emailUsage?.percent_used ?? 0) >= 90}
            data-testid="div-run-drip-now"
            title={(emailUsage?.percent_used ?? 0) >= 90 ? `Paused — Resend at ${emailUsage.percent_used}%` : ""}
            className="btn-outline-ink rounded-full h-10 px-4 border-brand/40 text-brand hover:bg-brand-soft disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send size={13} className="mr-1.5" /> Send drip now
          </Button>
          <DiversifiedImportDialog pw={pw} open={importOpen} setOpen={setImportOpen} onImported={() => load(filter)} />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="diversified-dashboard">
        <DMini label="Total contacts" value={d.total_contacts} />
        <DMini label="Individual" value={d.by_segment.individual} color="text-brand" />
        <DMini label="Organizational" value={d.by_segment.organizational} color="text-amber-700" />
        <DMini label="Unassigned" value={d.by_segment.unassigned} color="text-ink/55" />
        <DMini label="Email 1 delivered" value={d.delivered_by_step.email_1 || 0} />
        <DMini label="Email 2 delivered" value={d.delivered_by_step.email_2 || 0} />
        <DMini label="Email 3 delivered" value={d.delivered_by_step.email_3 || 0} />
        <DMini label="Opens" value={d.engagement.opens} />
        <DMini label="Clicks · Individual CTA" value={d.engagement.clicks_individual} color="text-brand" />
        <DMini label="Clicks · Org CTA" value={d.engagement.clicks_organizational} color="text-amber-700" />
        <DMini label="Org form submissions" value={d.org_inquiries} color="text-emerald-700" />
        <DMini label="Follow-up needed" value={d.follow_up_needed} color="text-amber-700" />
      </div>

      <div className="border-t border-[var(--line)] pt-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList className="bg-bone border border-[var(--line)]">
              <TabsTrigger value="all" data-testid="div-filter-all">All</TabsTrigger>
              <TabsTrigger value="unassigned" data-testid="div-filter-unassigned">Unassigned ({d.by_segment.unassigned})</TabsTrigger>
              <TabsTrigger value="individual" data-testid="div-filter-individual">Individual ({d.by_segment.individual})</TabsTrigger>
              <TabsTrigger value="organizational" data-testid="div-filter-organizational">Organizational ({d.by_segment.organizational})</TabsTrigger>
            </TabsList>
          </Tabs>
          {selected.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-ink/60 font-mono-spec uppercase tracking-wider">{selected.length} selected</span>
              <Button size="sm" onClick={() => assign("individual")} disabled={busy} data-testid="bulk-assign-individual" className="btn-ink rounded-full h-8 px-3"><User size={12} className="mr-1" /> Individual</Button>
              <Button size="sm" onClick={() => assign("organizational")} disabled={busy} data-testid="bulk-assign-organizational" className="bg-amber-600 hover:bg-amber-700 text-white rounded-full h-8 px-3"><Building2 size={12} className="mr-1" /> Organizational</Button>
              <Button size="sm" variant="outline" onClick={() => assign(null)} disabled={busy} className="btn-outline-ink rounded-full h-8 px-3">Clear</Button>
              <button onClick={clearSel} className="text-ink/55 hover:text-ink p-1"><XIcon size={14} /></button>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-[var(--line)] overflow-hidden bg-bone overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-[var(--line)] hover:bg-transparent">
                <TableHead className="w-10"><input type="checkbox" data-testid="div-select-all" checked={contacts.length > 0 && selected.length === contacts.length} onChange={(e) => e.target.checked ? selectAll() : clearSel()} /></TableHead>
                <TableHead className="text-ink/60">Name</TableHead>
                <TableHead className="text-ink/60">Email</TableHead>
                <TableHead className="text-ink/60">Segment</TableHead>
                <TableHead className="text-ink/60">Step</TableHead>
                <TableHead className="text-ink/60">Engagement</TableHead>
                <TableHead className="text-ink/60 text-right">Quick assign</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.length === 0 && (
                <TableRow className="border-[var(--line)] hover:bg-transparent">
                  <TableCell colSpan={7} className="text-center text-ink/50 py-12">No contacts in this view. Use the &ldquo;Import contacts&rdquo; button to get started.</TableCell>
                </TableRow>
              )}
              {contacts.map((c) => {
                const eng = c.opens + c.clicks_individual + c.clicks_organizational;
                return (
                  <TableRow key={c.id} data-testid={`div-row-${c.id}`} className="border-[var(--line)] hover:bg-white">
                    <TableCell><input type="checkbox" checked={isSelected(c.email)} onChange={() => toggleSelect(c.email)} data-testid={`div-checkbox-${c.id}`} /></TableCell>
                    <TableCell className="text-ink">{c.first_name || "—"}</TableCell>
                    <TableCell className="text-ink/75 text-sm">{c.email}</TableCell>
                    <TableCell>
                      {c.segment === "individual" && <Badge variant="outline" className="border-brand/40 text-brand bg-brand-soft text-[10px]">Individual</Badge>}
                      {c.segment === "organizational" && <Badge variant="outline" className="border-amber-200 text-amber-800 bg-amber-50 text-[10px]">Organizational</Badge>}
                      {!c.segment && <Badge variant="outline" className="border-ink/15 text-ink/55 bg-white text-[10px]">Unassigned</Badge>}
                    </TableCell>
                    <TableCell className="text-ink/65 font-mono-spec text-xs">{c.step}/3{c.active ? " · active" : ""}</TableCell>
                    <TableCell className="text-ink/65 text-xs">
                      {eng === 0 ? "—" : (
                        <span>{c.opens > 0 && <>👁 {c.opens} </>}{c.clicks_individual > 0 && <span className="text-brand">▸ ind {c.clicks_individual} </span>}{c.clicks_organizational > 0 && <span className="text-amber-700">▸ org {c.clicks_organizational}</span>}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => assign("individual", [c.email])} className="btn-outline-ink rounded-full h-7 px-2 text-[10px]">Ind</Button>
                        <Button size="sm" variant="outline" onClick={() => assign("organizational", [c.email])} className="btn-outline-ink rounded-full h-7 px-2 text-[10px]">Org</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function DMini({ label, value, color }) {
  return (
    <div className="rounded-xl border border-[var(--line)] bg-bone p-3">
      <div className="text-[10px] uppercase tracking-wider text-ink/55 font-mono-spec">{label}</div>
      <div className={`font-display text-2xl tracking-[-0.025em] mt-0.5 ${color || "text-ink"}`}>{(value || 0).toLocaleString()}</div>
    </div>
  );
}

function DiversifiedImportDialog({ pw, open, setOpen, onImported }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setText(""); setResult(null); };

  const run = async () => {
    if (!text.trim()) { toast.error("Paste your contact list first."); return; }
    setBusy(true);
    try {
      const res = await adminPost("/admin/diversified/import", pw, { contacts_text: text });
      setResult(res);
      if (res.ok) {
        toast.success(`${res.created} new + ${res.updated} updated.`);
        onImported?.();
      } else {
        toast.error(res.message || "Import failed.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="div-import-trigger" className="btn-ink rounded-full h-10 px-5"><Upload size={13} className="mr-1.5" /> Import contacts</Button>
      </DialogTrigger>
      <DialogContent className="bg-white border border-[var(--line)] text-ink max-w-2xl" data-testid="div-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-[-0.025em]">Import to AI Build Lab Diversified Outreach</DialogTitle>
          <DialogDescription className="text-ink/65">
            Paste your contact list. Same format as standard bulk import (<code className="px-1 py-0.5 bg-bone rounded text-xs">Name &lt;email&gt;</code> or bare emails, comma/newline separated). Contacts land here in <strong>this list only</strong> &mdash; they do NOT enter the default 9-email nurture funnel. Existing leads get updated with the new list tag (no duplicates).
          </DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="space-y-4 pt-2">
            <Textarea data-testid="div-import-text" value={text} onChange={(e) => setText(e.target.value)} rows={12} placeholder="Tiffany Daniels <td@aol.com>, Rebecca Hinson <info@example.org>, bare@email.com..." className="bg-bone border border-[var(--line)] text-ink font-mono text-[13px] focus-visible:ring-brand focus-visible:ring-offset-0" />
            <p className="text-xs text-ink/60 leading-relaxed">After import, contacts appear below as <strong>Unassigned</strong>. Review and assign each one as Individual or Organizational. Assigning a segment activates the 3-email drip (Day 0/4/8).</p>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
              <div className="font-display text-3xl text-emerald-800">{result.created} new <span className="font-serif-em italic font-light">+ {result.updated} updated</span></div>
              <p className="text-sm text-emerald-900/80 mt-1">All contacts are now in the &ldquo;AI Build Lab Diversified Outreach&rdquo; list, awaiting your segment assignment.</p>
            </div>
            {result.invalid && result.invalid.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900 font-mono">
                {result.invalid.length} unparseable lines.
              </div>
            )}
          </div>
        )}
        <DialogFooter className="pt-3 gap-2 flex-wrap">
          {result ? (
            <>
              <Button variant="outline" onClick={reset} className="btn-outline-ink rounded-full h-10 px-4">Import more</Button>
              <Button onClick={() => { setOpen(false); reset(); }} className="btn-ink rounded-full h-10 px-5">Done</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setOpen(false)} className="btn-outline-ink rounded-full h-10 px-4">Cancel</Button>
              <Button onClick={run} disabled={busy || !text.trim()} data-testid="div-import-run" className="btn-ink rounded-full h-10 px-5">{busy ? <Loader2 className="animate-spin" size={14} /> : "Import contacts"}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
